<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, Modal, ModalBody, ModalFooter } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { DocumentUploadForm } from '@client/pages/manage/assets/modals/shared';

  import { AssetsService } from '@client/services/assets';

  const { addNotification } = getNotificationsContext();

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  export let show = false;
  export let refetch = false;

  let isUpdating = false;
  let currentRulenames = [];
  let invalidRulename = false;
  let fileData = {};
  let files = [];
  let noncompliant = false;
  let rulename = '';
  let uploadingFile = false;
  let viewonly = false;

  let pdfViewer;
  let pageNumber = 0;
  let totalPages = 0;
  let isPdfLoaded = false;

  const onKeyUp = (event) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  const onClose = () => {
    fileData = {};
    files = [];
    noncompliant = false;
    rulename = '';
    viewonly = false;

    show = false;
  };

  const onRemoveFile = () => {
    files = [];
    fileData = {};
    uploadingFile = false;
  };

  const goToPage = (page) => {
    pdfViewer.goToPage(page);
  };

  const handlePageChanged = (event) => {
    pageNumber = event.detail.pageNumber;
  };

  const handleLoadedSuccess = (event) => {
    totalPages = event.detail.totalPages;
    pageNumber = 1;
    isPdfLoaded = true;
  };

  /** @returns {Promise<boolean>} whether the file landed with a usable filename. */
  const onFileUpload = async () => {
    uploadingFile = true;

    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    uploadingFile = false;

    const { hash, filesize, filename } = response ?? {};

    if (response?.status >= 400 || !filename) {
      isUpdating = false;

      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: files[0].name } }), 'error');

      return false;
    }

    fileData = { ...fileData, hash, filesize, filename };

    return true;
  };

  const onUploadDocument = async () => {
    isUpdating = true;

    // Without this the document is saved with no filename/hash and the
    // assets list cannot render it.
    if (!(await onFileUpload())) {
      return;
    }

    const data = {
      rulename: rulename,
      noncompliant: noncompliant,
      viewonly: viewonly,
      hash: fileData.hash,
      filesize: fileData.filesize,
      filename: fileData.filename
    };

    const response = await AssetsService.addDocument(data);

    isUpdating = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: rulename } }), 'error');

      return;
    }

    notify($format('label.SUCCESS.ASSET_UPLOAD', { values: { asset: rulename } }));

    setTimeout(() => (show = false), 200);

    refetch = true;
  };

  onMount(async () => {
    const response = await AssetsService.getAllDocuments();

    currentRulenames = response.map(({ rulename }) => rulename);
  });

  $: invalidRulename = currentRulenames.includes(rulename.toLowerCase());
  $: isDisabled = !files.length || uploadingFile || invalidRulename || !rulename.length;
</script>

<Modal bind:isOpen={show} close={false} size="lg" fullscreen="lg">
  <Loader backdrop show={isUpdating} />

  <ModalBody id="document-modal" class="document-form-modal">
    <button class="close" on:click={onClose}>
      <Icon name="x" />
    </button>

    <div>
      <h1>{$format('label.DOCUMENT_MODAL_NEW_DOCUMENT')}</h1>
      <p>{$format('label.DESCRIPTION.DOCUMENT_MODAL_NEW_DOCUMENT')}</p>
    </div>

    <div class="modal-body">
      <DocumentUploadForm
        bind:rulename
        bind:files
        bind:viewonly
        bind:noncompliant
        bind:invalidRulename
        bind:currentRulenames
        bind:pdfViewer
        bind:pageNumber
        bind:totalPages
        bind:isPdfLoaded
        {onRemoveFile}
        {goToPage}
        {handlePageChanged}
        {handleLoadedSuccess}
        inputId="document"
      />
    </div>
  </ModalBody>
  <ModalFooter>
    <Button color="primary" on:click={onUploadDocument} disabled={isDisabled}>
      {$format('label.DOCUMENT_MODAL_UPLOAD_DOCUMENT')}
    </Button>
  </ModalFooter>
</Modal>

<svelte:window on:keyup={onKeyUp} />

<style lang="scss" src="./document-modal.scss"></style>
